import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CheckCircle2, Clock, Crown, Heart, Lock, MapPin, Package, Repeat, Timer, TrendingDown, TrendingUp, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "../api";
import { useMember, label } from "../MemberContext";
import { Brand } from "../TrainingShell";
import { DIFF_LABEL, H1, Overline, PageLoader, TierBadge, ChipSelect, TIER_LABEL } from "../ui";
import { CoachTip, FocusNote, Posture, SafetyHint, Tags, VideoPlayer, WarmupNote } from "../ExerciseExtras";


function Paywall({ ex }) {
  const login = ex.lock_reason === "login";
  return (
    <div className="border border-[#c1ff72]/40 bg-[#c1ff72]/[0.04] p-8" data-testid="exercise-paywall">
      {login ? <Lock className="w-7 h-7 text-[#c1ff72] mb-4" /> : <Crown className="w-7 h-7 text-[#c1ff72] mb-4" />}
      <p className="font-['Bebas_Neue'] text-3xl text-white mb-2">{login ? "Kostenlos anmelden, um weiterzulesen" : `Teil von ${TIER_LABEL[ex.lock_reason]}`}</p>
      <p className="text-sm text-[#A1A1AA] mb-6">{login ? "Mit einem kostenlosen Konto siehst du Anleitung, Video, Sätze, Pausen und häufige Fehler." : "Mit der passenden Mitgliedschaft erhältst du sofort Zugriff auf diese Übung inklusive Video."}</p>
      {!login && <Link to="/training/mitgliedschaft" className="px-6 py-3 bg-[#c1ff72] text-black font-bold uppercase tracking-wider text-sm" data-testid="paywall-upgrade">Mitgliedschaft ansehen</Link>}
      {login && <div className="flex gap-3"><Link to="/training/registrieren" className="px-6 py-3 bg-[#c1ff72] text-black font-bold uppercase tracking-wider text-sm" data-testid="paywall-register">Konto erstellen</Link>
        <Link to={`/training/login?next=/training/uebung/${ex.slug}`} className="px-6 py-3 border border-white/20 text-white uppercase tracking-wider text-sm" data-testid="paywall-login">Anmelden</Link></div>}
    </div>
  );
}

const Fact = ({ icon: Icon, k, v }) => v ? <div className="p-5 border-r border-b border-white/10"><Icon className="w-4 h-4 text-[#c1ff72] mb-3" /><p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{k}</p><p className="text-white mt-1">{v}</p></div> : null;

const ListBlock = ({ title, items, icon: Icon, color, testId }) => items?.length ? (
  <div data-testid={testId}><p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-4">{title}</p>
    <ul className="space-y-3">{items.map((x) => <li key={x} className="flex gap-3 text-[#D4D4D8]"><Icon className={`w-5 h-5 shrink-0 ${color}`} />{x}</li>)}</ul></div>
) : null;

function Steps({ ex }) {
  return (
    <ol className="space-y-5" data-testid="exercise-steps">
      {ex.steps.map((s, i) => (
        <li key={s} className="flex gap-5"><span className="font-['Bebas_Neue'] text-4xl leading-none text-[#c1ff72] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span><p className="text-[#D4D4D8] leading-relaxed pt-1">{s}</p></li>
      ))}
    </ol>
  );
}

function CompleteDialog({ ex, open, onClose, onDone }) {
  const [mins, setMins] = useState(ex.duration_min);
  const [feeling, setFeeling] = useState(3);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const d = await api(`/me/completions/${ex.exercise_id}`, { method: "POST", body: { duration_min: Number(mins), feeling } }); onDone(d.stats, d.message); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none" data-testid="complete-dialog">
        <DialogHeader><DialogTitle className="font-['Bebas_Neue'] text-3xl font-normal">Einheit eintragen</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div><p className="text-sm text-[#A1A1AA] mb-2">Wie lange hast du trainiert?</p>
            <input type="number" min="1" max="240" value={mins} onChange={(e) => setMins(e.target.value)} className="w-28 h-11 px-3 bg-[#0A0A0A] border border-white/15 text-white" data-testid="complete-minutes" /> <span className="text-sm text-[#71717A]">Minuten</span></div>
          <div><p className="text-sm text-[#A1A1AA] mb-2">Wie anstrengend war es?</p>
            <ChipSelect options={[1, 2, 3, 4, 5].map((n) => ({ id: n, label: ["Sehr leicht", "Leicht", "Mittel", "Hart", "Sehr hart"][n - 1] }))} value={feeling} onChange={setFeeling} testPrefix="complete-feeling" /></div>
          <button disabled={busy} onClick={save} className="w-full h-12 bg-[#c1ff72] text-black font-bold uppercase tracking-wider" data-testid="complete-save">Speichern</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Actions({ ex, setEx }) {
  const [open, setOpen] = useState(false);
  const fav = async () => {
    try { const { favorite } = await api(`/me/favorites/${ex.exercise_id}`, { method: "POST" }); setEx({ ...ex, favorite }); } catch (e) { toast.error(e.message); }
  };
  const done = (stats, message) => {
    setOpen(false); setEx({ ...ex, completed_count: (ex.completed_count || 0) + 1 });
    toast.success(stats.streak_days > 1 ? `${message || "Stark!"} ${stats.streak_days} Tage in Folge trainiert.` : message || "Einheit gespeichert – jede Wiederholung zählt.");
  };
  return (
    <div className="flex flex-wrap gap-3">
      <button onClick={() => setOpen(true)} className="h-12 px-6 bg-[#c1ff72] text-black font-bold uppercase tracking-wider flex items-center gap-2 active:scale-95 transition-transform" data-testid="exercise-complete-button"><Check className="w-5 h-5" />Als erledigt markieren</button>
      <button onClick={fav} className="h-12 px-5 border border-white/20 text-white flex items-center gap-2" data-testid="exercise-fav-button"><Heart className={`w-5 h-5 ${ex.favorite ? "fill-[#c1ff72] text-[#c1ff72]" : ""}`} />{ex.favorite ? "Favorit" : "Merken"}</button>
      {ex.completed_count > 0 && <span className="h-12 flex items-center gap-2 text-sm text-[#A1A1AA]" data-testid="exercise-completed-count"><CheckCircle2 className="w-4 h-4 text-[#c1ff72]" />{ex.completed_count}× absolviert</span>}
      <CompleteDialog ex={ex} open={open} onClose={() => setOpen(false)} onDone={done} />
    </div>
  );
}

function Body({ ex }) {
  return (
    <div className="grid lg:grid-cols-3 gap-12 mt-12">
      <div className="lg:col-span-2 space-y-12">
        <WarmupNote ex={ex} />
        <div><p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-6">Schritt für Schritt</p><Steps ex={ex} /></div>
        <Posture ex={ex} />
        <div className="grid sm:grid-cols-2 gap-10">
          <ListBlock title="Häufige Fehler" items={ex.common_mistakes} icon={XCircle} color="text-red-400" testId="exercise-mistakes" />
          <ListBlock title="Richtig ausführen" items={ex.tips} icon={CheckCircle2} color="text-[#c1ff72]" testId="exercise-tips" />
        </div>
      </div>
      <div className="space-y-4 self-start">
      <CoachTip ex={ex} />
      <div className="space-y-px bg-white/10" data-testid="exercise-variants">
        {ex.easier && ex.easier !== "–" && <div className="bg-[#121212] p-6"><p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2"><TrendingDown className="w-4 h-4" />Leichter</p><p className="text-[#D4D4D8]">{ex.easier}</p></div>}
        {ex.harder && ex.harder !== "–" && <div className="bg-[#121212] p-6"><p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2"><TrendingUp className="w-4 h-4" />Schwieriger</p><p className="text-[#D4D4D8]">{ex.harder}</p></div>}
      </div>
      </div>
    </div>
  );
}

export default function ExerciseDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { meta, member } = useMember();
  const [ex, setEx] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setEx(null); api(`/exercises/${slug}`).then(setEx).catch((e) => setErr(e.message)); }, [slug, member]);
  const L = (g, ids) => (ids || []).map((i) => label(meta, g, i)).join(", ");
  return (
    <div className="min-h-[100dvh] bg-[#0A0A0A] text-white font-['Outfit']" data-testid="exercise-detail-page">
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 h-16 flex items-center gap-4">
          <button onClick={() => (window.history.length > 1 ? nav(-1) : nav("/training/bibliothek"))} className="text-[#A1A1AA] hover:text-white" data-testid="exercise-back"><ArrowLeft className="w-5 h-5" /></button>
          <Brand to={member ? "/training/start" : "/training"} />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 lg:px-10 pt-0 sm:pt-10 pb-24">
        {err ? <p className="text-[#A1A1AA]" data-testid="exercise-error">{err}</p> : !ex || !meta ? <PageLoader /> : (
          <>
            <VideoPlayer ex={ex} />
            {!ex.locked && <FocusNote ex={ex} />}
            <div className="mt-8 flex flex-wrap items-center gap-3"><Overline>{L("categories", ex.categories)}</Overline><TierBadge tier={ex.access} testId="exercise-tier" /></div>
            <H1 className="mt-3 !text-5xl sm:!text-6xl" data-testid="exercise-title">{ex.title}</H1>
            <p className="mt-5 max-w-3xl text-lg text-[#A1A1AA] font-light" data-testid="exercise-goal"><span className="text-white">Ziel:</span> {ex.goal}</p>
            <div className="mt-5"><Tags ex={ex} testId="exercise-tags" /></div>
            <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-white/10 bg-[#121212] mt-10" data-testid="exercise-facts">
              <Fact icon={Clock} k="Dauer" v={`ca. ${ex.duration_min} Min`} />
              <Fact icon={TrendingUp} k="Schwierigkeit" v={DIFF_LABEL[ex.difficulty]} />
              <Fact icon={Package} k="Equipment" v={L("equipment", ex.equipment) || "Ohne Material"} />
              <Fact icon={MapPin} k="Geeignet für" v={L("locations", ex.locations)} />
              {!ex.locked && <><Fact icon={Repeat} k="Sätze" v={ex.sets} /><Fact icon={Repeat} k="Wiederholungen" v={ex.reps} /><Fact icon={Timer} k="Pause" v={ex.rest} /></>}
              <Fact icon={CheckCircle2} k="Empfohlen für" v={`${L("levels", ex.levels)} · ${L("age_groups", ex.age_groups)}`} />
            </div>
            <div className="mt-10">{ex.locked ? <Paywall ex={ex} /> : <Actions ex={ex} setEx={setEx} />}</div>
            {!ex.locked && <Body ex={ex} />}
            <SafetyHint />
          </>
        )}
      </main>
    </div>
  );
}
